import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, IconButton, Chip, CircularProgress, TextField,
    Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Tooltip, Snackbar,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import RefreshIcon from '@material-ui/icons/Refresh';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import ArchiveIcon from '@material-ui/icons/Archive';
import PublishIcon from '@material-ui/icons/Publish';
import UnpublishedIcon from '@material-ui/icons/RemoveCircleOutline';

import { programTemplates } from '../../../services/dq_insider.service';
import { formatDate } from '../../../utils/utils';

// Mirrors LEARNING_PROGRAM_PUBLISH_STATUS in my-dq-api/src/utils/constants.js.
const PUBLISH_STATUS = { DRAFT: 10, PUBLISHED: 25 };

const useStyles = makeStyles(() => ({
    container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
    title: { fontSize: 24, fontWeight: 700 },
    subtitle: { color: '#999', marginTop: 4 },
    tableWrap: { background: '#fff', borderRadius: 8, overflow: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
    nameCell: { fontWeight: 600 },
    codeText: { fontSize: 11, color: '#999' },
    ownerText: { fontSize: 12 },
    actionsCell: { whiteSpace: 'nowrap' },
    centered: { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60 },
    metaField: { width: 90 },
}));

const StatusChip = ({ status }) => {
    const published = status === PUBLISH_STATUS.PUBLISHED;
    return (
        <Chip
            size="small"
            label={published ? 'Published' : 'Draft'}
            style={{
                background: published ? '#E8F5E9' : '#F5F5F5',
                color: published ? '#2E7D32' : '#757575',
                fontWeight: 600,
            }}
        />
    );
};

// generation_state comes from computeGenerationState on the backend
// (program_templates.adm.service.js) — distinguishes "hasn't been built yet"
// from "actively generating" from "the ML server failed" (ProgressStatus: -1)
// from "ready". Independent of Draft/Published: a template can be ready but
// still unpublished (admin hasn't reviewed it yet), or published but stuck
// mid-regenerate after an edit.
const GENERATION_STATE_CONFIG = {
    not_started: { label: 'Not Built', bg: '#F5F5F5', color: '#757575' },
    generating: { label: 'Generating…', bg: '#FFF3E0', color: '#E65100' },
    failed: { label: 'Failed', bg: '#FFEBEE', color: '#C62828' },
    ready: { label: 'Ready', bg: '#E3F2FD', color: '#1565C0' },
};

// Clickable — every state can be checked, but FAILED is where it matters most:
// getTemplateStatusAdminService returns a message explaining that no failure
// reason is available from this API and where to look instead (ML server logs).
const ReadyChip = ({ state, onClick }) => {
    const cfg = GENERATION_STATE_CONFIG[state] || GENERATION_STATE_CONFIG.not_started;
    return (
        <Chip
            size="small"
            label={cfg.label}
            onClick={onClick}
            style={{ background: cfg.bg, color: cfg.color, fontWeight: 600, cursor: 'pointer' }}
        />
    );
};

const Program_Templates_Page = () => {
    const classes = useStyles();
    const [templates, setTemplates] = useState([]);
    const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 20 });
    const [loading, setLoading] = useState(false);
    const [snackMsg, setSnackMsg] = useState('');
    const [busyCode, setBusyCode] = useState(null);

    // Inline category/order editor — the only content this page still edits.
    // Everything else (name/description/slides/etc.) is authored by the real
    // creator in dq-seal-client.
    const [editingCode, setEditingCode] = useState(null);
    const [editCategory, setEditCategory] = useState('');
    const [editOrder, setEditOrder] = useState('');

    const fetchList = useCallback((page = pagination.page, limit = pagination.limit) => {
        setLoading(true);
        programTemplates.listData(page, limit)
            .then(res => {
                if (res?.error) {
                    setSnackMsg(res?.message || res?.error_message || 'Failed to load templates.');
                    return;
                }
                setTemplates(res?.data?.templates || []);
                if (res?.data?.pagination) setPagination(res.data.pagination);
            })
            .catch(() => setSnackMsg('Failed to load templates.'))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { fetchList(1, 20); }, [fetchList]);

    const handleCheckStatus = async (row) => {
        try {
            const res = await programTemplates.getStatus(row.code);
            if (res?.error) {
                setSnackMsg(res?.message || res?.error_message || 'Failed to check status.');
                return;
            }
            const { generation_state, message } = res?.data || {};
            const cfg = GENERATION_STATE_CONFIG[generation_state] || GENERATION_STATE_CONFIG.not_started;
            setSnackMsg(message || `${row.full_name || row.code}: ${cfg.label}`);
        } catch (e) {
            setSnackMsg('Failed to check status.');
        }
    };

    const handleTogglePublish = async (row) => {
        const publish = row.status !== PUBLISH_STATUS.PUBLISHED;
        if (publish && !row.is_ready) {
            setSnackMsg('Complete all sessions before publishing.');
            return;
        }
        setBusyCode(row.code);
        try {
            const res = await programTemplates.setPublishStatus(row.code, publish);
            if (res?.error) {
                setSnackMsg(res?.message || res?.error_message || 'Failed to update publish status.');
                return;
            }
            setSnackMsg(publish ? 'Template published.' : 'Template moved back to draft.');
            fetchList();
        } catch (e) {
            setSnackMsg('Failed to update publish status.');
        } finally {
            setBusyCode(null);
        }
    };

    const handleArchive = async (row) => {
        if (!window.confirm(`Archive "${row.full_name || row.code}"? It will be removed from the gallery.`)) return;
        setBusyCode(row.code);
        try {
            const res = await programTemplates.archive(row.code);
            if (res?.error) {
                setSnackMsg(res?.message || res?.error_message || 'Failed to archive template.');
                return;
            }
            setSnackMsg('Template archived.');
            fetchList();
        } catch (e) {
            setSnackMsg('Failed to archive template.');
        } finally {
            setBusyCode(null);
        }
    };

    const handleStartEditMeta = (row) => {
        setEditingCode(row.code);
        setEditCategory(row.template_category || '');
        setEditOrder(row.template_order_no ?? '');
    };

    const handleCancelEditMeta = () => {
        setEditingCode(null);
    };

    const handleSaveEditMeta = async (row) => {
        setBusyCode(row.code);
        try {
            const res = await programTemplates.updateMeta(row.code, {
                template_category: editCategory,
                template_order_no: editOrder === '' ? null : Number(editOrder),
            });
            if (res?.error) {
                setSnackMsg(res?.message || res?.error_message || 'Failed to save category/order.');
                return;
            }
            setEditingCode(null);
            fetchList();
        } catch (e) {
            setSnackMsg('Failed to save category/order.');
        } finally {
            setBusyCode(null);
        }
    };

    return (
        <Box className={classes.container}>
            <Box className={classes.headerRow}>
                <Box>
                    <Typography className={classes.title}>Program Templates</Typography>
                    <Typography className={classes.subtitle} variant="body2">
                        Manage DQ showcase templates. Content is authored by flagged Template
                        Creator insiders in dq-seal-client — grant access from an insider's
                        profile under Organizations → Licenses &amp; Billing.
                    </Typography>
                </Box>
                <IconButton onClick={() => fetchList()} title="Refresh">
                    <RefreshIcon />
                </IconButton>
            </Box>

            <Box className={classes.tableWrap}>
                {loading ? (
                    <Box className={classes.centered}><CircularProgress /></Box>
                ) : templates.length === 0 ? (
                    <Box className={classes.centered}>
                        <Typography style={{ color: '#999' }}>
                            No templates yet. Grant Template Creator access to an insider, then
                            they can build one in dq-seal-client.
                        </Typography>
                    </Box>
                ) : (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Owner</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell align="center">Order</TableCell>
                                <TableCell align="center">Sessions</TableCell>
                                <TableCell align="center">Ready</TableCell>
                                <TableCell align="center">Status</TableCell>
                                <TableCell>Updated</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates.map(row => {
                                const isEditing = editingCode === row.code;
                                const ownerLabel = row.owner
                                    ? (row.owner.email
                                        || [row.owner.first_name, row.owner.last_name].filter(Boolean).join(' ')
                                        || row.owner.dq_insider_id)
                                    : (row.insider_id || '—');
                                return (
                                    <TableRow key={row.code} hover>
                                        <TableCell>
                                            <div className={classes.nameCell}>{row.full_name || '(untitled)'}</div>
                                            <div className={classes.codeText}>{row.code}</div>
                                        </TableCell>
                                        <TableCell className={classes.ownerText}>{ownerLabel}</TableCell>
                                        <TableCell>
                                            {isEditing ? (
                                                <TextField
                                                    value={editCategory}
                                                    onChange={(e) => setEditCategory(e.target.value)}
                                                    placeholder="Category"
                                                    size="small"
                                                />
                                            ) : (row.template_category || '—')}
                                        </TableCell>
                                        <TableCell align="center">
                                            {isEditing ? (
                                                <TextField
                                                    className={classes.metaField}
                                                    value={editOrder}
                                                    onChange={(e) => setEditOrder(e.target.value)}
                                                    placeholder="Order"
                                                    type="number"
                                                    size="small"
                                                />
                                            ) : (row.template_order_no ?? '—')}
                                        </TableCell>
                                        <TableCell align="center">{row.no_of_sessions ?? 0}</TableCell>
                                        <TableCell align="center">
                                            <ReadyChip state={row.generation_state} onClick={() => handleCheckStatus(row)} />
                                        </TableCell>
                                        <TableCell align="center"><StatusChip status={row.status} /></TableCell>
                                        <TableCell>{row.updatedAt ? formatDate(row.updatedAt) : '—'}</TableCell>
                                        <TableCell align="right" className={classes.actionsCell}>
                                            {isEditing ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <span>
                                                            <IconButton size="small" disabled={busyCode === row.code} onClick={() => handleSaveEditMeta(row)}>
                                                                <CheckIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <span>
                                                            <IconButton size="small" disabled={busyCode === row.code} onClick={handleCancelEditMeta}>
                                                                <CloseIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Edit category/order">
                                                    <span>
                                                        <IconButton size="small" disabled={busyCode === row.code} onClick={() => handleStartEditMeta(row)}>
                                                            <EditIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title={row.status === PUBLISH_STATUS.PUBLISHED ? 'Unpublish' : 'Publish'}>
                                                <span>
                                                    <IconButton size="small" disabled={busyCode === row.code} onClick={() => handleTogglePublish(row)}>
                                                        {row.status === PUBLISH_STATUS.PUBLISHED
                                                            ? <UnpublishedIcon fontSize="small" />
                                                            : <PublishIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip title="Archive">
                                                <span>
                                                    <IconButton size="small" disabled={busyCode === row.code} onClick={() => handleArchive(row)}>
                                                        <ArchiveIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}
            </Box>

            {pagination.total > pagination.limit && (
                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={(pagination.page || 1) - 1}
                    rowsPerPage={pagination.limit || 20}
                    rowsPerPageOptions={[20, 50, 100]}
                    onPageChange={(_, newPage) => fetchList(newPage + 1, pagination.limit)}
                    onRowsPerPageChange={(e) => fetchList(1, parseInt(e.target.value, 10))}
                />
            )}

            <Snackbar
                open={!!snackMsg}
                message={snackMsg}
                autoHideDuration={4000}
                onClose={() => setSnackMsg('')}
            />
        </Box>
    );
};

export default Program_Templates_Page;
